import { useRouter } from 'next/router'
import { MouseEvent } from 'react'
import styled from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'

export interface SimpleMedicalTip {
  tipId: number
  thumbnailUrl: string
  writerName: string
  title: string
}
export interface Props {
  medicalTips: SimpleMedicalTip[]
  onClickMore?: () => void
  onClickMedicalTip?: (id: number) => void
}

function MedicalTipWidget({ medicalTips, onClickMore, onClickMedicalTip }: Props): JSX.Element {
  const router = useRouter()

  const handleClickMore = (e: MouseEvent) => {
    e.preventDefault()
    onClickMore?.()
    void router.push('/community/medical-tips')
  }

  const handleClickMedicalTip = (e: MouseEvent, medicalTip: SimpleMedicalTip) => {
    e.preventDefault()
    onClickMedicalTip?.(medicalTip.tipId)
    void router.push(`/community/medical-tips/${medicalTip.tipId}`)
  }

  return (
    <StyledContainer>
      <StyledHeaderWrapper>
        <StyledTitle>언니네 꿀팁</StyledTitle>

        <StyledMoreMedicalTipWrapper>
          <StyledMoreMedicalTip href="/community/medical-tips" onClick={handleClickMore}>
            전체보기
          </StyledMoreMedicalTip>
          <IcArrowRight width={16} height={16} />
        </StyledMoreMedicalTipWrapper>
      </StyledHeaderWrapper>

      <StyledMedicalTipList>
        {medicalTips.map((medicalTip) => (
          <StyledMedicalTipItem key={medicalTip.tipId} onClick={(e) => handleClickMedicalTip(e, medicalTip)}>
            <StyledMedicalTipThumbnail src={medicalTip.thumbnailUrl} alt={medicalTip.title} />
            <StyledMedicalTpWriterName>{medicalTip.writerName}</StyledMedicalTpWriterName>
            <StyledMedicalTipTitle>
              <a href={`/community/medical-tips/${medicalTip.tipId}`}>{medicalTip.title}</a>
            </StyledMedicalTipTitle>
          </StyledMedicalTipItem>
        ))}
      </StyledMedicalTipList>
    </StyledContainer>
  )
}

const StyledContainer = styled.div``
const StyledHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`
const StyledTitle = styled.h2`
  ${({ theme }) => theme.typography.title2};
  color: ${({ theme }) => theme.colors.black};
  margin: 0px;
`

const StyledMoreMedicalTipWrapper = styled.div`
  display: flex;
  align-items: center;
`
const StyledMoreMedicalTip = styled(Link)`
  ${({ theme }) => theme.typography.description2};
  color: ${({ theme }) => theme.colors.gray800};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    text-decoration-color: ${({ theme }) => theme.colors.gray400};
  }
`

const StyledMedicalTipList = styled.ul`
  list-style: none;
  margin: 0px;
  padding: 0px;
  width: 100%;
  display: grid;
  gap: 24px 16px;
  grid-template-columns: repeat(2, 1fr);
`
const StyledMedicalTipItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
`
const StyledMedicalTipThumbnail = styled.img`
  width: 100%;
  border-radius: 8px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
`
const StyledMedicalTpWriterName = styled.p`
  ${({ theme }) => theme.typography.description2};
  color: ${({ theme }) => theme.colors.gray800};
  margin: 0px;
  height: 20px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  display: -webkit-box;
  text-overflow: ellipsis;
`
const StyledMedicalTipTitle = styled.h3`
  ${({ theme }) => theme.typography.description1};
  margin: 0px;
  height: 48px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  text-overflow: ellipsis;
  a {
    ${({ theme }) => theme.typography.description1};
    color: ${({ theme }) => theme.colors.black};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
      text-decoration-color: ${({ theme }) => theme.colors.gray400};
    }
  }
`

export default MedicalTipWidget
